import React from 'react';
import { Square, TimerOff } from 'lucide-react';
import { autoStopReasonText, type AutoStopWarning } from './useAutoStop';
import { t } from '../../lib/i18n';

interface AutoStopBannerProps {
  warning: AutoStopWarning;
  onKeepRecording: () => void;
  onStopNow: () => void;
  isDarkMode: boolean;
}

/**
 * "Toplantı bitmiş görünüyor" uyarısı. Sayfadan bağımsız (App seviyesinde)
 * çiziliyor: kayıt sürerken kullanıcı başka bir sayfadaysa da görsün.
 */
export const AutoStopBanner: React.FC<AutoStopBannerProps> = ({
  warning,
  onKeepRecording,
  onStopNow,
  isDarkMode,
}) => (
  <div className="fixed top-16 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label={t('Kayıt otomatik bitirilecek', 'The recording will stop automatically')}
      className={`pointer-events-auto w-full max-w-xl flex items-center gap-3.5 pl-4 pr-3 py-3 rounded-2xl border shadow-2xl ${
        isDarkMode ? 'bg-[#19191C] border-amber-500/30 text-neutral-200' : 'bg-white border-amber-400/50 text-neutral-800'
      }`}
    >
      <TimerOff className="w-5 h-5 text-amber-400 shrink-0" />

      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold">{t('Toplantı bitmiş görünüyor', 'The meeting seems to be over')}</p>
        <p className="text-[12.5px] text-neutral-500 leading-snug">
          {autoStopReasonText(warning.reason)} {t('Kayıt', 'The recording will be stopped and summarized in')}{' '}
          <span className="font-mono tabular-nums text-amber-400">
            {t(`${warning.remainingSeconds} sn`, `${warning.remainingSeconds}s`)}
          </span>
          {t(' içinde bitirilip özetlenecek.', '.')}
        </p>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={onKeepRecording}
          className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold transition-colors ${
            isDarkMode ? 'text-neutral-300 hover:bg-[#1F1F23] hover:text-[#F0EEE6]' : 'text-neutral-700 hover:bg-neutral-100'
          }`}
        >
          {t('Kayda devam et', 'Keep recording')}
        </button>
        <button
          onClick={onStopNow}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1DA4BA] hover:bg-[#3AC8E0] text-[#F0EEE6] text-[12.5px] font-semibold transition-colors"
        >
          <Square className="w-3 h-3 fill-current" />
          {t('Şimdi bitir', 'Stop now')}
        </button>
      </div>
    </div>
  </div>
);
